import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";

interface BillingStatus {
  billingConfigured: boolean;
  hasSubscription: boolean;
  seatsPurchased: number;
  seatsInUse: number;
  seatsAvailable: number;
  pendingSeats: number | null;
  seatsEffectiveAt: string | null;
  renewsAt: string | null;
  cancelAtPeriodEnd: boolean;
  billingStatus: string | null;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "renewal";
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function money(pence: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency.toUpperCase() }).format(pence / 100);
  } catch {
    return `£${(pence / 100).toFixed(2)}`;
  }
}

/** How far through the current annual term we are, derived from the renewal date. */
function periodProgress(renewsAtIso: string | null): { pct: number; daysLeft: number } | null {
  if (!renewsAtIso) return null;
  const end = new Date(renewsAtIso).getTime();
  if (!Number.isFinite(end)) return null;
  const start = end - 365 * 24 * 3600 * 1000;
  const now = Date.now();
  const pct = Math.max(0, Math.min(100, Math.round(((now - start) / (end - start)) * 100)));
  const daysLeft = Math.max(0, Math.ceil((end - now) / (24 * 3600 * 1000)));
  return { pct, daysLeft };
}

interface Invoice {
  id: string;
  number: string | null;
  created: number;
  amountPaid: number;
  currency: string;
  status: string;
  url: string | null;
  pdf: string | null;
}

/** Seat control for one subscription-backed licence: shows purchased / in use /
 * available, and drives the asymmetric increase (immediate) / decrease (scheduled
 * to renewal, no refund) policy through /api/customer/billing/*. */
export function SeatManager({ licenceId }: { licenceId: string }) {
  const [s, setS] = useState<BillingStatus | null>(null);
  const [target, setTarget] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ mode: "increase" | "decrease"; seats: number } | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  async function load() {
    try {
      const res = await api.get<BillingStatus>(`/customer/billing/status?licenceId=${licenceId}`);
      setS(res);
      setTarget(res.pendingSeats ?? res.seatsPurchased);
    } catch {
      setS(null);
      return;
    }
    try {
      const inv = await api.get<{ invoices: Invoice[] }>(`/customer/billing/invoices?licenceId=${licenceId}`);
      setInvoices(inv.invoices ?? []);
    } catch {
      /* invoices are non-critical - the panel still works without them */
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenceId]);

  if (!s || !s.billingConfigured || !s.hasSubscription) return null;

  async function apply(seats: number) {
    setBusy(true);
    setError(null);
    try {
      await api.post("/customer/billing/change-seats", { licenceId, seats });
      setConfirm(null);
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.message === "seats_below_active_users") {
        // The endpoint returns a structured 409; api throws its `error` message.
        // Customers can't remove devices themselves - a device stops counting
        // automatically after 30 days without checking in. So the guidance is to
        // wait for unused devices to drop off, not to "remove" anything.
        setError(
          "That's fewer seats than the devices currently using this licence. A device frees its seat automatically after 30 days without use — once your unused devices drop off, you can reduce your seats."
        );
      } else {
        setError(err instanceof Error ? err.message : "Couldn't update seats");
      }
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  }

  async function cancelScheduled() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/customer/billing/cancel-scheduled", { licenceId });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't cancel the scheduled change");
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/customer/billing/resume", { licenceId });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't resume the subscription");
    } finally {
      setBusy(false);
    }
  }

  async function openPortal() {
    setBusy(true);
    try {
      const res = await api.post<{ url?: string }>("/customer/billing/portal", { licenceId });
      if (res.url) window.location.href = res.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't open the billing portal");
    } finally {
      setBusy(false);
    }
  }

  function requestChange() {
    if (target === s!.seatsPurchased && s!.pendingSeats == null) return;
    setError(null);
    setConfirm({ mode: target > s!.seatsPurchased ? "increase" : "decrease", seats: target });
  }

  return (
    <div className="lic-details" style={{ marginTop: 18 }}>
      <div className="lic-key-label">Seats</div>

      {/* Scheduled reduction banner */}
      {s.pendingSeats != null && (
        <div className="warning-box" style={{ marginBottom: 12 }}>
          Reducing to <strong>{s.pendingSeats} seat{s.pendingSeats === 1 ? "" : "s"}</strong> on {fmtDate(s.seatsEffectiveAt)}. You keep your
          current {s.seatsPurchased} seats until then — no refund or credit is issued.
          <div style={{ marginTop: 8 }}>
            <button className="secondary" onClick={cancelScheduled} disabled={busy}>Cancel scheduled change</button>
          </div>
        </div>
      )}

      {/* Cancel-at-period-end banner */}
      {s.cancelAtPeriodEnd && (
        <div className="warning-box" style={{ marginBottom: 12 }}>
          Your subscription is scheduled to cancel on {fmtDate(s.renewsAt)}.
          <div style={{ marginTop: 8 }}>
            <button className="secondary" onClick={resume} disabled={busy}>Resume subscription</button>
          </div>
        </div>
      )}

      {/* Over-allocation nudge: more devices in use than seats bought -> add seats
          (customers never remove devices; unused ones free up after 30 days). */}
      {s.seatsInUse > s.seatsPurchased && (
        <div className="warning-box" style={{ marginBottom: 12 }}>
          You're using <strong>{s.seatsInUse}</strong> device{s.seatsInUse === 1 ? "" : "s"} but have{" "}
          <strong>{s.seatsPurchased}</strong> seat{s.seatsPurchased === 1 ? "" : "s"} — you're over your allocation. Add seats
          below to stay covered. A device frees its seat automatically after 30 days without use.
        </div>
      )}

      <div className="lic-facts" style={{ marginBottom: 14 }}>
        <div className="lic-fact"><span className="lic-fact-label">Purchased</span><span className="lic-fact-value">{s.seatsPurchased}</span></div>
        <div className="lic-fact"><span className="lic-fact-label">In use</span><span className="lic-fact-value">{s.seatsInUse}</span></div>
        <div className="lic-fact"><span className="lic-fact-label">Available</span><span className="lic-fact-value">{s.seatsAvailable}</span></div>
        <div className="lic-fact"><span className="lic-fact-label">Renews</span><span className="lic-fact-value" style={{ fontSize: 13.5 }}>{fmtDate(s.renewsAt)}</span></div>
      </div>

      {(() => {
        const prog = periodProgress(s.renewsAt);
        if (!prog) return null;
        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "#6b6b76", marginBottom: 6 }}>
              <span>{prog.pct}% through this year</span>
              <span>{prog.daysLeft} day{prog.daysLeft === 1 ? "" : "s"} to renewal</span>
            </div>
            <div style={{ height: 7, borderRadius: 999, background: "#ececf1", overflow: "hidden" }}>
              <div style={{ width: `${prog.pct}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg,#FFC400,var(--accent) 60%,#7B2FF7)" }} />
            </div>
          </div>
        );
      })()}

      {confirm ? (
        <div className="warning-box">
          {confirm.mode === "increase" ? (
            <p style={{ margin: 0 }}>
              Increase to <strong>{confirm.seats} seats</strong>? You'll be charged a <strong>prorated amount today</strong> for the extra
              seats for the rest of this term. The new seats are available immediately.
            </p>
          ) : (
            <p style={{ margin: 0 }}>
              Reduce to <strong>{confirm.seats} seats</strong>? You'll keep your current {s.seatsPurchased} seats until <strong>{fmtDate(s.renewsAt)}</strong>,
              when the reduction takes effect. <strong>No refund or credit</strong> is issued.
            </p>
          )}
          <div className="button-row" style={{ marginTop: 10 }}>
            <button className="primary" onClick={() => apply(confirm.seats)} disabled={busy}>
              {busy ? "Working…" : confirm.mode === "increase" ? "Confirm & pay" : "Schedule reduction"}
            </button>
            <button className="secondary" onClick={() => setConfirm(null)} disabled={busy}>Back</button>
          </div>
        </div>
      ) : (
        <div className="lic-seat-edit">
          <button className="secondary" onClick={() => setTarget((t) => Math.max(1, t - 1))} disabled={busy}>−</button>
          <input type="number" min={1} max={100000} value={target} onChange={(e) => setTarget(Math.max(1, Number(e.target.value) || 1))} />
          <button className="secondary" onClick={() => setTarget((t) => t + 1)} disabled={busy}>+</button>
          <button className="primary" onClick={requestChange} disabled={busy || (target === s.seatsPurchased && s.pendingSeats == null)}>
            Update seats
          </button>
        </div>
      )}

      {error && <p className="error-text" style={{ marginTop: 10 }}>{error}</p>}

      {invoices.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div className="lic-key-label">Invoices</div>
          <div style={{ marginTop: 6 }}>
            {invoices.map((inv) => (
              <div key={inv.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "9px 0", borderTop: "1px solid #efeeee", fontSize: 13.5 }}>
                <span style={{ color: "#3a3736", minWidth: 92 }}>{fmtDate(new Date(inv.created * 1000).toISOString())}</span>
                <span style={{ fontWeight: 600 }}>{money(inv.amountPaid, inv.currency)}</span>
                <span style={{ textTransform: "capitalize", color: inv.status === "paid" ? "#12855f" : "#8a8685", fontSize: 12.5 }}>{inv.status}</span>
                {inv.url || inv.pdf ? (
                  <a href={inv.url || inv.pdf || "#"} target="_blank" rel="noreferrer" className="link-btn">View</a>
                ) : (
                  <span style={{ width: 32 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="muted-subtitle" style={{ marginTop: 14 }}>
        <button className="link-btn" onClick={openPortal} disabled={busy}>Open billing portal (payment method &amp; receipts)</button>
      </p>
    </div>
  );
}
